import React, { useState, ReactNode, HTMLAttributes, useRef } from 'react';
import moment from 'moment';
import Calendar, { OnArgs } from 'react-calendar';
import SelectInput from '../Select';
import Button from '../Button';
import { ArrowLeftIcon, ArrowRightIcon, CalendarDays } from '../Icons';
import cn from '@/utils/cn';
import useHomeContext from '@/hooks/useHomeContext';
import { TCalendarInput, TCalendarInputPiece, TSelectOptions } from './types';
import { useClickOutside } from '@/hooks/useClickOutside';
import { TDateOption } from '@/features/home/hooks/useDateFilter';

export const DATE_FORMAT = 'DD MMM yyyy';
const LAST_7_DAYS = 'Last 7 days';
const LAST_MONTH = 'Last month';
const LAST_90_DAYS = 'Last 90 days';
const YEAR_TO_DATE = 'Year to date';
export const DATE_FILTER_FOCUS_NAME = 'date';

const ButtonClassName = cn([
  'bg-base-3',
  'border',
  'border-aux-gray',
  'text-dark-navy-30',
  'text-sm',
  'rounded-full',
  'block',
  'py-2',
  'flex',
  'flex-row',
  'justify-between',
  'items-center',
  'gap-x-2',
  'hover:bg-emerge-base-2',
  'active:bg-emerge-base-2',
  'focus:outline-none',
  'focus:ring',
  'focus:ring-aux-gray',
  'focus:bg-emerge-base-2'
]);
const DateRangePickerClassName = cn([
  // Was z-10 - ties with DataTableFooterNew's sticky floating footer (also z-10, only present
  // when the SELECTED_ACCOUNT feature flag turns on the new table footer). Neither this
  // dropdown's nor the table's own wrapper establishes an isolated stacking context, so equal
  // z-index falls back to DOM order - Filters renders before the table, so the footer (later
  // in the DOM) painted on top of this dropdown wherever their boxes overlapped. z-1000 matches
  // the convention already used for floating popovers elsewhere (DatepickerInput,
  // SimpleDropdownMenu) that need to reliably sit above ordinary page content.
  'z-1000',
  'absolute',
  'mt-1',
  'bg-white',
  'rounded-lg',
  'shadow-lg',
  'w-auto',
  'h-auto',
  'p-4',
  'flex',
  'flex-col',
  'gap-4'
]);
const CalendarClassName = cn(['']);
interface DateRangeInputProps extends HTMLAttributes<HTMLButtonElement> {
  onDatesApplied?: (
    startDate: TCalendarInputPiece,
    endDate: TCalendarInputPiece,
    range: TDateOption,
    isDefault: boolean
  ) => void;
  onDropdownChange: (data: { label: string; value: string | undefined }) => void;
  startDate: TCalendarInputPiece;
  onStartDateChange: (date: Date) => void;
  endDate: TCalendarInputPiece;
  onEndDateChange: (date: Date) => void;
  dropdownValue: { label: string; value: string | undefined };
  onResetOptions: () => void;
  header?: ReactNode;
  optionsContainerClass?: string;
  focusName?: string;
  show?: boolean;
  onForceClose?: () => void;
  /** Override the calendar icon shown in the pill button. Defaults to CalendarDays. */
  icon?: ReactNode;
}

const generateOption = (option: string) => ({
  label: option,
  value: option
});

const options = [
  { label: 'Custom view', value: undefined },
  generateOption(LAST_7_DAYS),
  generateOption(LAST_MONTH),
  generateOption(LAST_90_DAYS),
  generateOption(YEAR_TO_DATE)
];

const startOfDay = (date: Date) => moment(date).startOf('day').toDate();
const endOfDay = (date: Date) => moment(date).endOf('day').toDate();

const DateRangeInput: React.FC<DateRangeInputProps> = ({
  optionsContainerClass,
  className,
  onDatesApplied: handler,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onDropdownChange: onSelectChange,
  dropdownValue,
  onResetOptions,
  show,
  onClick,
  onForceClose,
  icon
}) => {
  const { setDateFilter } = useHomeContext()!;
  const filterRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(filterRef, () => {
    if (onForceClose && show) {
      onForceClose();
    }
  });

  const defaults = {
    today: new Date(),
    dateRange: options[1]
  };
  const sevenDaysAgo = moment(defaults.today).subtract(7, 'days').toDate();
  const ninetyDaysAgo = moment(defaults.today).subtract(90, 'days').toDate();

  const [isOpen, setIsOpen] = useState(show);
  const [activeDate, setActiveDate] = useState<Date>(sevenDaysAgo);
  const [unappliedDates, setUnappliedDates] = useState<{
    startDate: TCalendarInputPiece;
    endDate: TCalendarInputPiece;
    selectValue: { label: string; value: string | undefined } | null;
  }>({
    startDate: null,
    endDate: null,
    selectValue: null
  });

  const setDateRangeFilter = (
    startDate: TCalendarInputPiece,
    endDate: TCalendarInputPiece,
    rangeName: TDateOption
  ) => {
    setDateFilter({
      startDate: startOfDay(startDate!),
      endDate: endOfDay(endDate!),
      rangeName
    });
  };

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handleOnToggle = (event: any) => {
    if (onClick) {
      onClick(event);
    }

    setIsOpen((prev) => {
      if (prev === true) {
        if (unappliedDates.endDate && unappliedDates.startDate && unappliedDates.selectValue) {
          onStartDateChange(unappliedDates.startDate);
          onEndDateChange(unappliedDates.endDate);
          onSelectChange(unappliedDates.selectValue);
          setActiveDate(unappliedDates.startDate);

          setUnappliedDates({
            startDate: null,
            endDate: null,
            selectValue: null
          });
        }
      }

      return !prev;
    });
  };

  const handleOnChange = (selectedDates: TCalendarInput) => {
    if (selectedDates && Array.isArray(selectedDates)) {
      onStartDateChange(selectedDates[0]!);
      onEndDateChange(selectedDates[1]!);
      return;
    }

    onStartDateChange(selectedDates!);
    onEndDateChange(selectedDates!);
  };

  const handleApply = () => {
    if (handler) {
      const isDefault = startDate === sevenDaysAgo && endDate === defaults.today;
      handler(startOfDay(startDate!), endOfDay(endDate!), dropdownValue, isDefault);
    }

    setIsOpen(false);
  };

  const handleReset = (applyChange = false) => {
    onSelectChange(defaults.dateRange);
    onStartDateChange(sevenDaysAgo);
    onEndDateChange(defaults.today);
    setActiveDate(sevenDaysAgo);

    if (!applyChange) {
      setUnappliedDates({
        startDate,
        endDate,
        selectValue: dropdownValue
      });
      return;
    }

    if (onResetOptions) {
      onResetOptions();
    }

    setDateRangeFilter(sevenDaysAgo, defaults.today, defaults.dateRange);
  };

  const handleActiveStartDateChange = ({ action, activeStartDate, value }: OnArgs) => {
    if (action === 'next' || action === 'prev') {
      setActiveDate(activeStartDate!);
    }

    if (action === 'onChange' && Array.isArray(value)) {
      onSelectChange(options[0]);
    }
  };

  const handleOnSelectedOption = (option: TSelectOptions, optionIndex: number) => {
    onSelectChange(options[optionIndex]);
    switch (option.value) {
      case LAST_7_DAYS: {
        onStartDateChange(sevenDaysAgo);
        onEndDateChange(endOfDay(defaults.today));
        setActiveDate(sevenDaysAgo);
        break;
      }
      case LAST_MONTH: {
        const lastMonth = moment().subtract(1, 'months');
        const firstOfTheMonth = lastMonth.startOf('month').toDate();
        const endOfTheMonth = lastMonth.endOf('month').toDate();
        onStartDateChange(firstOfTheMonth);
        onEndDateChange(endOfDay(endOfTheMonth));
        setActiveDate(firstOfTheMonth);
        break;
      }
      case LAST_90_DAYS: {
        onStartDateChange(ninetyDaysAgo);
        onEndDateChange(endOfDay(defaults.today));
        setActiveDate(ninetyDaysAgo);
        break;
      }
      case YEAR_TO_DATE: {
        const firstOfTheYear = moment().startOf('year').toDate();
        onStartDateChange(firstOfTheYear);
        onEndDateChange(endOfDay(defaults.today));
        setActiveDate(firstOfTheYear);
        break;
      }
    }
  };

  return (
    <div ref={filterRef} className="relative">
      <Button
        onClick={handleOnToggle}
        className={cn(ButtonClassName, className)}
        variant="secondary">
        {icon ?? <CalendarDays />} {moment(startDate).format(DATE_FORMAT)} {' - '}{' '}
        {moment(endDate).format(DATE_FORMAT)}
      </Button>

      <div className={cn(DateRangePickerClassName, optionsContainerClass, !isOpen ? 'hidden' : '')}>
        <div className="text-sm font-medium text-emerge-navy leading-5">Date range</div>
        <SelectInput
          className="w-full rounded-lg bg-emerge-base-3 px-2.5 py-3 border-emerge-navy-4"
          optionsContainerClass="w-full right-0"
          value={dropdownValue.label}
          options={options}
          onOptionSelected={handleOnSelectedOption}
        />
        <Calendar
          className={CalendarClassName}
          onChange={handleOnChange}
          maxDate={moment().endOf('day').toDate()}
          value={!endDate ? startDate : [startDate, endDate]}
          nextLabel={<ArrowRightIcon size="md" />}
          prevLabel={<ArrowLeftIcon size="md" />}
          showDoubleView={true}
          activeStartDate={activeDate}
          onActiveStartDateChange={handleActiveStartDateChange}
          selectRange={true}
          view="month"
          allowPartialRange={true}
        />
        <div className="flex flex-row gap-x-2 p-2">
          <Button className="flex-1" onClick={handleApply}>
            Apply
          </Button>
          <Button className="flex-1" variant="secondary" onClick={() => handleReset()}>
            Reset
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DateRangeInput;
